// web/src/screens/admin/admin_shell.ts
//
// Shared chrome for every admin screen: header + breadcrumb nav. Plan
// 11-07's Step/Action/TextSet/Text screens reuse renderAdminShell verbatim.
//
// T-11-09a (StoredXSS) lock: every breadcrumb label is inserted via
// element.textContent — never via raw-HTML setters. The label string may
// eventually come from the user-edited short_code, so this is the
// canonical mitigation site for the admin shell.
//
// CONTEXT D-Claude-discretion (T-11-13 Spoofing): NO admin link in kid UI.
// Admin is discoverable only via direct URL — keeps a child from accidentally
// stumbling into it during play.

import { getTTSCoverage } from "../../api/admin.js";
import type { AdminTTSCoverageDTO } from "../../types/api.js";

export interface AdminBreadcrumb {
  label: string;
  href?: string;
}

// CustomEvent name dispatched after a successful TTS rebuild — listened for
// by the shell's coverage banner so a fresh rebuild updates the banner
// before the server-side cache TTL expires.
export const TTS_COVERAGE_INVALIDATE_EVENT = "admin:tts-coverage-invalidate";

const COVERAGE_CACHE_KEY = "admin.tts.coverage.v1";
const COVERAGE_CACHE_TTL_MS = 30_000;

interface CachedCoverage {
  at: number;
  dto: AdminTTSCoverageDTO;
}

function readCachedCoverage(): AdminTTSCoverageDTO | null {
  try {
    const raw = sessionStorage.getItem(COVERAGE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedCoverage;
    if (Date.now() - parsed.at > COVERAGE_CACHE_TTL_MS) return null;
    return parsed.dto;
  } catch {
    return null;
  }
}

function writeCachedCoverage(dto: AdminTTSCoverageDTO): void {
  try {
    const payload: CachedCoverage = { at: Date.now(), dto };
    sessionStorage.setItem(COVERAGE_CACHE_KEY, JSON.stringify(payload));
  } catch { /* sessionStorage may be unavailable (privacy mode) — silently skip */ }
}

function clearCachedCoverage(): void {
  try { sessionStorage.removeItem(COVERAGE_CACHE_KEY); } catch { /* ignore */ }
}

// renderAdminShell wipes `root`, mounts the header + breadcrumb nav, and
// returns a freshly-created <main> element where the calling screen should
// render its body. The caller is responsible for unmounting their own
// listeners; the shell installs none.
export function renderAdminShell(root: HTMLElement, breadcrumbs: AdminBreadcrumb[]): HTMLElement {
  document.body.className = "admin-ui"; // strip any kid-app theme color; opt out of the 64px tap-target floor (style.css)
  root.replaceChildren();

  const header = document.createElement("header");
  header.className = "admin-header";
  header.setAttribute(
    "style",
    "display: flex; flex-direction: column; gap: 4px; padding: 12px 24px; background: #1f2937; color: #f9fafb; font-family: system-ui, sans-serif;",
  );

  const title = document.createElement("h1");
  title.textContent = "Admin"; // T-11-09: textContent only
  title.setAttribute("style", "margin: 0; font-size: 18px; font-weight: 600; letter-spacing: 0.04em;");
  header.appendChild(title);

  // Top-level section nav — present on every admin screen so Games / Game log
  // / TTS / Settings are reachable from anywhere (the breadcrumb below shows
  // *where you are*; this row shows *where you can go*). The current section
  // (matched by path prefix) renders non-link and bold.
  const topNav = document.createElement("nav");
  topNav.setAttribute("style", "display: flex; flex-wrap: wrap; gap: 14px; align-items: center; font-size: 14px; padding: 2px 0;");
  const here = window.location.pathname;
  // v3.2 (migration 0038): Texts top-nav entry removed (texts are now strict
  // children of TextSets and edited inline within the TextSet editor).
  const sections: Array<{ label: string; href: string; match: (p: string) => boolean }> = [
    { label: "Game log", href: "/admin/sessions", match: (p) => p === "/admin" || p === "/admin/" || p === "/admin/sessions" || p.startsWith("/admin/sessions/") || p.startsWith("/admin/attempts/") },
    { label: "Games", href: "/admin/games", match: (p) => p === "/admin/games" || p.startsWith("/admin/games/") },
    { label: "Shared texts", href: "/admin/textsets", match: (p) => p === "/admin/textsets" || p.startsWith("/admin/textsets/") },
    { label: "TTS", href: "/admin/tts", match: (p) => p === "/admin/tts" || p.startsWith("/admin/tts/") },
    { label: "Settings", href: "/admin/settings", match: (p) => p === "/admin/settings" || p.startsWith("/admin/settings/") },
  ];
  for (const sec of sections) {
    if (sec.match(here)) {
      const span = document.createElement("span");
      span.textContent = sec.label;
      span.setAttribute("style", "color: #f9fafb; font-weight: 600;");
      topNav.appendChild(span);
    } else {
      const a = document.createElement("a");
      a.textContent = sec.label;
      a.setAttribute("href", sec.href);
      a.setAttribute("style", "color: #93c5fd; text-decoration: none;");
      a.addEventListener("click", (e) => {
        e.preventDefault();
        history.pushState({}, "", sec.href);
        window.dispatchEvent(new PopStateEvent("popstate"));
      });
      topNav.appendChild(a);
    }
  }
  header.appendChild(topNav);

  // Hide the breadcrumb row when it would just echo the top-nav selection
  // (e.g. "Games" under Games). Show it only when it adds location info.
  if (breadcrumbs.length > 1) {
    const nav = document.createElement("nav");
    nav.setAttribute("style", "display: flex; flex-wrap: wrap; gap: 4px; align-items: center; font-size: 14px;");
    for (let i = 0; i < breadcrumbs.length; i++) {
      const bc = breadcrumbs[i]!;
      if (bc.href) {
        const a = document.createElement("a");
        a.textContent = bc.label; // T-11-09: textContent only
        a.setAttribute("href", bc.href);
        a.setAttribute("style", "color: #93c5fd; text-decoration: none;");
        a.addEventListener("click", (e) => {
          e.preventDefault();
          const target = bc.href!;
          history.pushState({}, "", target);
          window.dispatchEvent(new PopStateEvent("popstate"));
        });
        nav.appendChild(a);
      } else {
        const span = document.createElement("span");
        span.textContent = bc.label; // T-11-09: textContent only
        span.setAttribute("style", "color: #f9fafb; font-weight: 500;");
        nav.appendChild(span);
      }
      if (i < breadcrumbs.length - 1) {
        const sep = document.createElement("span");
        sep.textContent = " / ";
        sep.setAttribute("style", "color: #6b7280;");
        nav.appendChild(sep);
      }
    }
    header.appendChild(nav);
  }
  root.appendChild(header);

  const coverageBanner = document.createElement("div");
  coverageBanner.className = "admin-coverage-banner";
  coverageBanner.style.display = "none";
  root.appendChild(coverageBanner);
  mountCoverageBanner(coverageBanner);

  const main = document.createElement("main");
  main.className = "admin-main";
  main.setAttribute(
    "style",
    "padding: 24px; font-family: system-ui, sans-serif; color: #111827; background: #f9fafb; min-height: calc(100dvh - 64px);",
  );
  root.appendChild(main);

  return main;
}

// mountCoverageBanner renders the "audio missing" warning into `host`. It
// fetches /api/admin/tts/coverage with a per-tab sessionStorage cache (30s
// TTL matching the server cache) so rapid nav between admin screens doesn't
// re-hit the network. The banner is suppressed on /admin/tts/* (those screens
// already expose the Build missing / Rebuild all buttons). Listens for the
// TTS_COVERAGE_INVALIDATE_EVENT to refresh after a successful rebuild.
function mountCoverageBanner(host: HTMLDivElement): void {
  const onTTSScreen = window.location.pathname.startsWith("/admin/tts");
  if (onTTSScreen) return;

  const render = (dto: AdminTTSCoverageDTO): void => {
    if (dto.total_missing <= 0) {
      host.style.display = "none";
      host.replaceChildren();
      return;
    }
    host.replaceChildren();
    host.setAttribute(
      "style",
      "display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 10px 24px; background: #fef3c7; color: #78350f; border-bottom: 1px solid #fbbf24; font-family: system-ui, sans-serif; font-size: 14px;",
    );

    const icon = document.createElement("span");
    icon.textContent = "⚠";
    icon.setAttribute("style", "font-size: 16px;");
    host.appendChild(icon);

    const msg = document.createElement("span");
    // textContent only — T-11-09a safe even though the strings here are
    // server-side label fields we don't fully control.
    const parts: string[] = [];
    if (dto.active_voice_missing > 0 && dto.active_voice_label) {
      parts.push(`Active voice “${dto.active_voice_label}” is missing audio for ${dto.active_voice_missing} of ${dto.total_phrases} in-use phrases.`);
    }
    const otherVoices = dto.voices_with_missing - (dto.active_voice_missing > 0 ? 1 : 0);
    if (otherVoices > 0) {
      parts.push(`${otherVoices} other voice${otherVoices === 1 ? "" : "s"} also have gaps.`);
    }
    if (parts.length === 0) {
      // Edge case: total_missing > 0 but neither active-voice nor "other"
      // matched (e.g. no active voice configured). Fall back to a generic line.
      parts.push(`Audio missing for ${dto.total_missing} phrase/voice combination${dto.total_missing === 1 ? "" : "s"}.`);
    }
    msg.textContent = parts.join(" ");
    host.appendChild(msg);

    const cta = document.createElement("a");
    cta.textContent = "Go to TTS →";
    cta.setAttribute("href", "/admin/tts");
    cta.setAttribute("style", "color: #92400e; font-weight: 600; text-decoration: underline; margin-left: auto;");
    cta.addEventListener("click", (e) => {
      e.preventDefault();
      history.pushState({}, "", "/admin/tts");
      window.dispatchEvent(new PopStateEvent("popstate"));
    });
    host.appendChild(cta);
  };

  const cached = readCachedCoverage();
  if (cached) {
    render(cached);
  }

  const refresh = (force: boolean): void => {
    if (!force) {
      const c = readCachedCoverage();
      if (c) { render(c); return; }
    } else {
      clearCachedCoverage();
    }
    getTTSCoverage().then((dto) => {
      writeCachedCoverage(dto);
      render(dto);
    }).catch(() => {
      // Coverage is non-critical. Don't surface fetch errors — the shell
      // shouldn't break if the operator hits this page mid-deploy.
    });
  };

  // Always refresh after the cached render so a stale cache eventually
  // converges. If there was no cache hit, this is the first fetch.
  refresh(false);

  const onInvalidate = (): void => { refresh(true); };
  window.addEventListener(TTS_COVERAGE_INVALIDATE_EVENT, onInvalidate);
  // The shell is re-mounted on every nav (root is wiped), so the host
  // element is removed before the next render. Detach the listener when
  // host leaves the DOM to avoid a slow leak across navigations.
  const observer = new MutationObserver(() => {
    if (!host.isConnected) {
      window.removeEventListener(TTS_COVERAGE_INVALIDATE_EVENT, onInvalidate);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// Small helper used across admin screens to surface short user-facing
// notices (Saved / Deleted / Error). Caller passes the parent element;
// the toast self-removes after `durationMs`. textContent only — keeps
// T-11-09a closed even for messages composed from API responses.
export function showAdminToast(parent: HTMLElement, msg: string, durationMs = 1800): void {
  const t = document.createElement("div");
  t.textContent = msg;
  t.setAttribute(
    "style",
    "position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: #111827; color: #f9fafb; padding: 10px 16px; border-radius: 6px; font-size: 14px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 1000;",
  );
  parent.appendChild(t);
  window.setTimeout(() => { t.remove(); }, durationMs);
}
